import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiService, LookupOption } from "../../services/api";

interface LookupsState {
  lookups: Record<string, LookupOption[]>;
  rawData: Record<string, any[]>; // Raw data for SubModule (without transformation)
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  lastFetched: Record<string, number>;
}

const initialState: LookupsState = {
  lookups: {},
  rawData: {},
  loading: {},
  errors: {},
  lastFetched: {},
};

export interface FetchLookupParams {
  lookupName: string;
  params?: {
    parentid?: string | number;
    lookuptype?: string;
  };
}

export const fetchLookup = createAsyncThunk<
  { lookupKey: string; options: LookupOption[] },
  FetchLookupParams,
  { rejectValue: { lookupKey: string; error: string } }
>("lookups/fetch", async ({ lookupName, params }, { rejectWithValue }) => {
  try {
    const payload = {
      parentid: params?.parentid || "",
      lookuptype: params?.lookuptype || "Select",
      // For get_all_ledgers, try to request all results
      // Backend might ignore these parameters, but we'll try
      ...(lookupName === "get_all_ledgers" ? { skip: 0, limit: 0 } : {}), // limit: 0 might mean "all" to backend
    };

    // Log lookup fetch start for get_all_ledgers and journal_ledgers
    if (lookupName === "get_all_ledgers" || lookupName === "journal_ledgers") {
      console.log(`[${lookupName}] Fetch Lookup Started:`, {
        lookupName,
        params,
        payload,
        timestamp: new Date().toISOString(),
      });
    }

    const options = await apiService.getLookupData(lookupName, payload);

    // Generate lookup key: for independent lookups use lookupName, for dependent use `${lookupName}_${parentid}`
    const lookupKey = params?.parentid
      ? `${lookupName}_${params.parentid}`
      : lookupName;

    // Log lookup fetch success for get_all_ledgers and journal_ledgers
    if (lookupName === "get_all_ledgers" || lookupName === "journal_ledgers") {
      console.log(`[${lookupName}] Fetch Lookup Success:`, {
        lookupKey,
        optionsCount: options.length,
        options: options,
        sample: options.slice(0, 5), // First 5 items
        timestamp: new Date().toISOString(),
      });
    }

    return { lookupKey, options };
  } catch (error) {
    const lookupKey = params?.parentid
      ? `${lookupName}_${params.parentid}`
      : lookupName;

    // Log lookup fetch error for get_all_ledgers and journal_ledgers
    if (lookupName === "get_all_ledgers" || lookupName === "journal_ledgers") {
      console.error(`[${lookupName}] Fetch Lookup Error:`, {
        lookupKey,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString(),
      });
    }

    return rejectWithValue({
      lookupKey,
      error: error instanceof Error ? error.message : "Failed to fetch lookup",
    });
  }
});

export const fetchDependentLookup = createAsyncThunk<
  { lookupKey: string; options: LookupOption[] },
  {
    lookupName: string;
    parentValue: string | number;
    lookuptype?: string;
  },
  { rejectValue: { lookupKey: string; error: string } }
>(
  "lookups/fetchDependent",
  async (
    { lookupName, parentValue, lookuptype = "Select" },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        parentid: String(parentValue),
        lookuptype: lookuptype,
      };

      const options = await apiService.getLookupData(lookupName, payload);
      const lookupKey = `${lookupName}_${parentValue}`;

      return { lookupKey, options };
    } catch (error) {
      const lookupKey = `${lookupName}_${parentValue}`;
      return rejectWithValue({
        lookupKey,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch dependent lookup",
      });
    }
  }
);

export const fetchSubModuleRawData = createAsyncThunk<
  { lookupKey: string; rawData: any[] },
  {
    lookupName: string;
    parentValue: string | number;
    lookuptype?: string;
  },
  { rejectValue: { lookupKey: string; error: string } }
>(
  "lookups/fetchSubModuleRawData",
  async (
    { lookupName, parentValue, lookuptype = "Search_Select" },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        parentid: String(parentValue),
        lookuptype: lookuptype,
      };

      const rawData = await apiService.getRawLookupData(lookupName, payload);
      // CRITICAL FIX: For independent lookups (empty parentValue), use just lookupName
      // For dependent lookups, use ${lookupName}_${parentValue}
      const lookupKey =
        parentValue === "" || parentValue === null || parentValue === undefined
          ? lookupName
          : `${lookupName}_${parentValue}`;

      return { lookupKey, rawData };
    } catch (error) {
      // CRITICAL FIX: Match the lookupKey generation logic
      const lookupKey =
        parentValue === "" || parentValue === null || parentValue === undefined
          ? lookupName
          : `${lookupName}_${parentValue}`;
      return rejectWithValue({
        lookupKey,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch SubModule raw data",
      });
    }
  }
);

const lookupsSlice = createSlice({
  name: "lookups",
  initialState,
  reducers: {
    clearLookup(state, action: PayloadAction<string>) {
      const lookupKey = action.payload;
      delete state.lookups[lookupKey];
      delete state.rawData[lookupKey];
      delete state.loading[lookupKey];
      delete state.errors[lookupKey];
      delete state.lastFetched[lookupKey];
    },
    clearAllLookups(state) {
      state.lookups = {};
      state.rawData = {};
      state.loading = {};
      state.errors = {};
      state.lastFetched = {};
    },
    addLookupItem(
      state,
      action: PayloadAction<{
        lookupKey: string;
        item: any;
        isRawData?: boolean;
      }>
    ) {
      const { lookupKey, item, isRawData } = action.payload;
      
      if (isRawData) {
        // Add to rawData
        if (!state.rawData[lookupKey]) {
          state.rawData[lookupKey] = [];
        }
        // Check if item already exists
        const existingIndex = state.rawData[lookupKey].findIndex(
          (existing: any) =>
            existing.id === item.id ||
            String(existing.id) === String(item.id) ||
            existing.value === item.id ||
            String(existing.value) === String(item.id)
        );
        if (existingIndex >= 0) {
          state.rawData[lookupKey][existingIndex] = item;
        } else {
          state.rawData[lookupKey].push(item);
        }
      } else {
        // Add to lookups (as LookupOption)
        if (!state.lookups[lookupKey]) {
          state.lookups[lookupKey] = [];
        }
        const lookupOption: LookupOption = {
          ...item,
          label: item.label || item.name || item.id,
          value: item.value || item.id,
        };
        // Check if item already exists
        const existingIndex = state.lookups[lookupKey].findIndex(
          (existing: any) =>
            existing.value === lookupOption.value ||
            String(existing.value) === String(lookupOption.value) ||
            existing.id === lookupOption.value ||
            String(existing.id) === String(lookupOption.value)
        );
        if (existingIndex >= 0) {
          state.lookups[lookupKey][existingIndex] = lookupOption;
        } else {
          state.lookups[lookupKey].push(lookupOption);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLookup.pending, (state, action) => {
        const { lookupName, params } = action.meta.arg;
        const lookupKey = params?.parentid
          ? `${lookupName}_${params.parentid}`
          : lookupName;
        state.loading[lookupKey] = true;
        state.errors[lookupKey] = null;
      })
      .addCase(fetchLookup.fulfilled, (state, action) => {
        const { lookupKey, options } = action.payload;
        state.lookups[lookupKey] = options;
        state.loading[lookupKey] = false;
        state.lastFetched[lookupKey] = Date.now();
        state.errors[lookupKey] = null;
      })
      .addCase(fetchLookup.rejected, (state, action) => {
        const lookupKey = action.meta.arg.params?.parentid
          ? `${action.meta.arg.lookupName}_${action.meta.arg.params.parentid}`
          : action.meta.arg.lookupName;
        state.loading[lookupKey] = false;
        state.errors[lookupKey] = action.payload?.error || "Unknown error";
      })
      .addCase(fetchDependentLookup.pending, (state, action) => {
        const { lookupName, parentValue } = action.meta.arg;
        const lookupKey = `${lookupName}_${parentValue}`;
        state.loading[lookupKey] = true;
        state.errors[lookupKey] = null;
      })
      .addCase(fetchDependentLookup.fulfilled, (state, action) => {
        const { lookupKey, options } = action.payload;
        state.lookups[lookupKey] = options;
        state.loading[lookupKey] = false;
        state.lastFetched[lookupKey] = Date.now();
        state.errors[lookupKey] = null;
      })
      .addCase(fetchDependentLookup.rejected, (state, action) => {
        const { lookupName, parentValue } = action.meta.arg;
        const lookupKey = `${lookupName}_${parentValue}`;
        state.loading[lookupKey] = false;
        state.errors[lookupKey] = action.payload?.error || "Unknown error";
      })
      .addCase(fetchSubModuleRawData.pending, (state, action) => {
        const { lookupName, parentValue } = action.meta.arg;
        // CRITICAL FIX: Match the lookupKey generation logic
        const lookupKey =
          parentValue === "" ||
          parentValue === null ||
          parentValue === undefined
            ? lookupName
            : `${lookupName}_${parentValue}`;
        state.loading[lookupKey] = true;
        state.errors[lookupKey] = null;
      })
      .addCase(fetchSubModuleRawData.fulfilled, (state, action) => {
        const { lookupKey, rawData } = action.payload;
        state.rawData[lookupKey] = rawData;
        state.loading[lookupKey] = false;
        state.lastFetched[lookupKey] = Date.now();
        state.errors[lookupKey] = null;
      })
      .addCase(fetchSubModuleRawData.rejected, (state, action) => {
        const { lookupName, parentValue } = action.meta.arg;
        // CRITICAL FIX: Match the lookupKey generation logic
        const lookupKey =
          parentValue === "" ||
          parentValue === null ||
          parentValue === undefined
            ? lookupName
            : `${lookupName}_${parentValue}`;
        state.loading[lookupKey] = false;
        state.errors[lookupKey] = action.payload?.error || "Unknown error";
      });
  },
});

export const { clearLookup, clearAllLookups, addLookupItem } = lookupsSlice.actions;
export default lookupsSlice.reducer;
